import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from "prop-types";
import {useSearchParams} from "react-router-dom";
import {ProgressSpinner} from "primereact/progressspinner";
import ErrorInfoComponent from "../../../general/ErrorInfoComponent";
import LastEventComp from "../../../LastEventComp";
import GasteingabeDialog from "./GasteingabeDialog";
import {addLastWatchedEvent, getFromLocalStorageWithDefault} from "../../../../util/localStorageZugriff";
import {TOURNAMENTSYSTEMS} from "../../../../constants/tournamentSystems";
import useTournamentLive from "./useTournamentLive";
import {BEREICH, ermittleBereiche} from "./bereiche";
import EventHeader from "./EventHeader";
import UebersichtBereich from "./UebersichtBereich";
import InfosBereich from "./InfosBereich";
import BereichInhalt from "./BereichInhalt";
import SpielerSuche, {ermittleMarkiertenSpieler, ermittleSpielernamen} from "./SpielerSuche";
import useSpielerHervorhebung from "./useSpielerHervorhebung";
import "./publicTournamentNeu.css";

/**
 * Neue öffentliche Turnieransicht. Die Inhalte der Reiter stammen aus der alten Ansicht (PublicTournament),
 * neu sind Kopfbereich, Navigation (fünf Bereiche mit Unterpunkten, auf dem Handy unten) und die Übersicht.
 * Bereich und Unterpunkt stehen in der URL (?tab=...&sub=...).
 */
function PublicTournamentNeu({tournamentId, userId, isLoggedIn, appCall}) {

    const live = useTournamentLive(tournamentId);
    const tournamentWrapper = live.tournamentWrapper;

    const [searchParams, setSearchParams] = useSearchParams();

    const gastInputLigaspieltag = getFromLocalStorageWithDefault("dh_ligaspieltag_gast_input", undefined);
    const [showGasteingabeDialog, setShowGasteingabeDialog] = useState(false);

    // Spielersuche: der gewählte Spieler wird im gesamten Inhaltsbereich hervorgehoben
    const inhaltRef = useRef(null);
    const [sucheEingabe, setSucheEingabe] = useState("");
    const spielernamen = useMemo(() => tournamentWrapper === undefined ? [] : ermittleSpielernamen(tournamentWrapper), [tournamentWrapper]);
    const markierterSpieler = ermittleMarkiertenSpieler(sucheEingabe, spielernamen);
    const ansichtKey = (tournamentWrapper !== undefined) + "|" + searchParams.get("tab") + "|" + searchParams.get("sub");
    const anzahlTreffer = useSpielerHervorhebung(inhaltRef, markierterSpieler, ansichtKey);

    useEffect(() => {
        if (tournamentWrapper === undefined || tournamentWrapper.turnierMain === undefined) {
            return;
        }
        addLastWatchedEvent(tournamentWrapper.turnierMain.id, tournamentWrapper.turnierMain.bezeichnung);
    }, [tournamentWrapper]);

    if (tournamentWrapper === undefined) {
        return <div className="dhpt-seite dhpt-laden">
            <ErrorInfoComponent infoMessage={undefined} errorMessage={live.errorMessage} onClearInfoMessage={() => ""}
                                onClearErrorMessage={() => live.setErrorMessage(undefined)}/>
            <ProgressSpinner style={{width: 100, height: 100}}/>
        </div>
    }

    const t = tournamentWrapper.turnierMain;
    const bereiche = ermittleBereiche(tournamentWrapper);
    const bereich = bereiche.find(b => b.id === searchParams.get("tab")) || bereiche[0];
    const sub = bereich.subs.find(s => s.id === searchParams.get("sub")) || bereich.subs[0];

    const wechsle = (bereichId, subId) => {
        const params = new URLSearchParams(searchParams);
        params.set("tab", bereichId);
        if (subId) {
            params.set("sub", subId);
        } else {
            params.delete("sub");
        }
        setSearchParams(params);
    }

    const hatSub = (bereichId, subId) => {
        const gefunden = bereiche.find(b => b.id === bereichId);
        return gefunden !== undefined && gefunden.subs.some(s => s.id === subId);
    }

    const isLigaspieltagUndGasteingabe = () => {
        return t.turniersystem === TOURNAMENTSYSTEMS.LIGASPIELTAG.id
            && gastInputLigaspieltag && gastInputLigaspieltag.tournamentid === t.id;
    }

    const getAktionen = () => {
        const aktionen = [];
        if (isLigaspieltagUndGasteingabe()) {
            aktionen.push({label: "Gasteingabe", icon: "pi pi-pencil", primaer: true, onClick: () => setShowGasteingabeDialog(true)});
        }
        if (t.tournamentstatus === "neu" && t.registrationAllowed && hatSub(BEREICH.TEILNEHMER, "anmeldung")) {
            aktionen.push({label: "Jetzt anmelden", icon: "pi pi-plus", primaer: true, onClick: () => wechsle(BEREICH.TEILNEHMER, "anmeldung")});
        }
        if (t.tournamentstatus === "gestartet") {
            if (hatSub(BEREICH.SPIELE, "live")) {
                aktionen.push({label: "Live ansehen", icon: "pi pi-eye", primaer: true, onClick: () => wechsle(BEREICH.SPIELE, "live")});
            }
            if (bereiche.some(b => b.id === BEREICH.SPIELE)) {
                aktionen.push({label: "Zum Spielplan", icon: "pi pi-th-large", onClick: () => wechsle(BEREICH.SPIELE, bereiche.find(b => b.id === BEREICH.SPIELE).subs.find(s => s.id !== "live")?.id)});
            }
        }
        if (t.tournamentstatus === "abgeschlossen" && bereiche.some(b => b.id === BEREICH.STATISTIK)) {
            aktionen.push({label: "Ergebnisse ansehen", icon: "pi pi-chart-bar", onClick: () => wechsle(BEREICH.STATISTIK)});
        }
        return aktionen;
    }

    const getInhalt = () => {
        if (bereich.id === BEREICH.UEBERSICHT) {
            return <div className="dhpt-inhalt-eigen">
                <UebersichtBereich tournamentWrapper={tournamentWrapper} liveGames={live.liveGames} bereiche={bereiche} onWechsle={wechsle}/>
            </div>
        }
        if (bereich.id === BEREICH.INFOS) {
            return <div className="dhpt-inhalt-eigen">
                <InfosBereich tournament={t}/>
            </div>
        }
        return <div className="dhpt-bestand">
            <BereichInhalt sub={sub.id} tournamentWrapper={tournamentWrapper} liveGames={live.liveGames}
                           userId={userId} isLoggedIn={isLoggedIn} appCall={appCall} tournamentId={tournamentId}
                           supportsWebPush={live.supportsWebPush} pushSpielerIds={live.pushSpielerIds} updateSubscriptions={live.updateSubscriptions}
                           markierterSpieler={markierterSpieler}
                           onRefresh={() => live.ladeDaten(tournamentId)}/>
        </div>
    }

    return <div className="dhpt-seite">
        <ErrorInfoComponent infoMessage={undefined} errorMessage={live.errorMessage} onClearInfoMessage={() => ""}
                            onClearErrorMessage={() => live.setErrorMessage(undefined)}/>
        <EventHeader tournamentWrapper={tournamentWrapper} verbunden={live.verbunden} appCall={appCall}
                     aktionen={getAktionen()} onRefresh={() => live.ladeDaten(tournamentId)}/>
        {!appCall &&
            <div className="dhpt-letzte-events">
                <LastEventComp currentId={t.id} onLoad={(id) => live.ladeDaten(id)}/>
            </div>
        }

        <nav className="dhpt-nav" role="tablist" aria-label="Bereiche">
            {bereiche.map(b => <button key={b.id} type="button" role="tab" aria-selected={b.id === bereich.id}
                                       className="dhpt-nav-button" onClick={() => wechsle(b.id)}>
                <i className={b.icon} aria-hidden="true"/>
                <span>{b.label}</span>
            </button>)}
        </nav>

        {bereich.id !== BEREICH.INFOS &&
            <div className="dhpt-werkzeuge">
                {bereich.subs.length > 1 &&
                    <div className="dhpt-subs" role="group" aria-label={bereich.label}>
                        {bereich.subs.map(s => <button key={s.id} type="button" aria-pressed={s.id === sub.id}
                                                       className="dhpt-sub-button" onClick={() => wechsle(bereich.id, s.id)}>
                            {s.farbe && <i className={"dhpt-punkt dhpt-punkt-" + s.farbe} aria-hidden="true"/>}
                            {s.icon && <i className={s.icon} aria-hidden="true"/>}
                            {s.label}
                        </button>)}
                    </div>
                }
                <SpielerSuche tournamentWrapper={tournamentWrapper} eingabe={sucheEingabe} onEingabe={setSucheEingabe}
                              markierterSpieler={markierterSpieler} anzahlTreffer={anzahlTreffer}/>
            </div>
        }

        <main ref={inhaltRef} className="dhpt-inhalt darthelfer-background-blue">
            {getInhalt()}
        </main>

        {showGasteingabeDialog && <GasteingabeDialog
            aktuellerBereich={t.ligaspieltagBereich}
            onRefresh={() => live.ladeDaten(t.id)}
            onClose={() => setShowGasteingabeDialog(false)}/>}
    </div>
}

PublicTournamentNeu.propTypes = {
    userId: PropTypes.string,
    isLoggedIn: PropTypes.bool.isRequired,
    tournamentId: PropTypes.string.isRequired,
    appCall: PropTypes.bool,
};

export default PublicTournamentNeu;
